import { useCallback, useSyncExternalStore } from "react";

export type TrainedModelRuntimePhase = "idle" | "loading" | "running" | "error";

export type TrainedModelRuntimeStatus = {
  phase: TrainedModelRuntimePhase;
  lastError: string | null;
  lastInferenceMs: number | null;
  updatedAt: number;
};

const DEFAULT_STATUS: TrainedModelRuntimeStatus = {
  phase: "idle",
  lastError: null,
  lastInferenceMs: null,
  updatedAt: 0,
};

const statusByNodeId = new Map<string, TrainedModelRuntimeStatus>();
const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

export function setTrainedModelRuntimeStatus(
  nodeId: string,
  patch: Partial<TrainedModelRuntimeStatus>
): void {
  if (!nodeId) return;
  const prev = statusByNodeId.get(nodeId) || DEFAULT_STATUS;
  statusByNodeId.set(nodeId, {
    phase: patch.phase ?? prev.phase,
    lastError: patch.lastError ?? prev.lastError,
    lastInferenceMs:
      patch.lastInferenceMs == null ? prev.lastInferenceMs : Number(patch.lastInferenceMs) || 0,
    updatedAt: Date.now(),
  });
  emitChange();
}

export function clearTrainedModelRuntimeStatus(nodeId: string): void {
  if (!nodeId) return;
  if (statusByNodeId.delete(nodeId)) {
    emitChange();
  }
}

export function getTrainedModelRuntimeStatus(nodeId: string): TrainedModelRuntimeStatus {
  if (!nodeId) return DEFAULT_STATUS;
  return statusByNodeId.get(nodeId) || DEFAULT_STATUS;
}

export function useTrainedModelRuntimeStatus(
  nodeId: string,
  options?: { enabled?: boolean }
): TrainedModelRuntimeStatus {
  const enabled = options?.enabled !== false;
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!enabled) return () => {};
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    [enabled]
  );
  const getSnapshot = useCallback(
    () => (enabled ? getTrainedModelRuntimeStatus(nodeId) : DEFAULT_STATUS),
    [enabled, nodeId]
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_STATUS);
}
